<script>
export default {
  name: "SearchableSelect",
  props: {
    options: { type: Array, default: () => [] }, // lista [{id, name}]
    modelValue: { type: [String, Number, Array], default: null },
    placeholder: { type: String, default: "Seleccionar" },
    disabled: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false }, // Modo multi-select
    filterDisabled: { type: Function, default: null } // Función para deshabilitar opciones dinámicamente
  },
  emits: ["update:modelValue"],
  data() {
    return {
      searchQuery: "",
      isOpen: false
    };
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  computed: {
    filteredOptions() {
      return this.options.filter(opt =>
        opt.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    selectedName() {
      if (this.multiple) {
        const selectedIds = Array.isArray(this.modelValue) ? this.modelValue : [];
        if (selectedIds.length === 0) return "";
        if (selectedIds.length === 1) {
          const sel = this.options.find(o => o.id === selectedIds[0]);
          return sel ? sel.name : "";
        }
        return `${selectedIds.length} seleccionados`;
      } else {
        const sel = this.options.find(o => o.id === this.modelValue);
        return sel ? sel.name : "";
      }
    },
    selectedOptions() {
      if (!this.multiple) return [];
      const selectedIds = Array.isArray(this.modelValue) ? this.modelValue : [];
      return this.options.filter(opt => selectedIds.includes(opt.id));
    }
  },
  methods: {
    selectOption(id) {
      if (this.multiple) {
        const currentSelection = Array.isArray(this.modelValue) ? [...this.modelValue] : [];
        const index = currentSelection.indexOf(id);
        
        if (index > -1) {
          // Deseleccionar
          currentSelection.splice(index, 1);
        } else {
          // Seleccionar
          currentSelection.push(id);
        }
        
        this.$emit("update:modelValue", currentSelection);
        this.searchQuery = "";
      } else {
        this.$emit("update:modelValue", id);
        this.isOpen = false;
        this.searchQuery = "";
      }
    },
    removeOption(id, event) {
      if (event) event.stopPropagation();
      if (!this.multiple) return;
      
      const currentSelection = Array.isArray(this.modelValue) ? [...this.modelValue] : [];
      const index = currentSelection.indexOf(id);
      if (index > -1) {
        currentSelection.splice(index, 1);
        this.$emit("update:modelValue", currentSelection);
      }
    },
    isSelected(id) {
      if (this.multiple) {
        const selectedIds = Array.isArray(this.modelValue) ? this.modelValue : [];
        // Normalizar tipos: convertir ambos a números para comparación
        const normalizedId = typeof id === 'string' ? parseInt(id, 10) : id;
        const isIncluded = selectedIds.some(selectedId => {
          const normalizedSelectedId = typeof selectedId === 'string' ? parseInt(selectedId, 10) : selectedId;
          return normalizedSelectedId === normalizedId;
        });
        return isIncluded;
      }
      return this.modelValue === id;
    },
    isOptionDisabled(option) {
      if (this.filterDisabled) {
        return this.filterDisabled(option);
      }
      return false;
    },
    handleBlur() {
      // Retraso mínimo para permitir que mousedown se procese antes de cerrar
      setTimeout(() => {
        this.isOpen = false;
        this.searchQuery = "";
      }, 100);
    },
    handleFocus() {
      if (!this.disabled) {
        this.isOpen = true;
      }
    },
    handleClickOutside(event) {
      // Cerrar dropdown si se hace click fuera del componente
      if (this.$el && !this.$el.contains(event.target)) {
        this.isOpen = false;
        this.searchQuery = "";
      }
    }
  }
};
</script>

<template>
  <div class="relative w-full">
    <!-- Input de búsqueda con chips para modo múltiple -->
    <div 
      v-if="multiple && selectedOptions.length > 0"
      class="flex flex-wrap gap-1 mb-2"
    >
      <span
        v-for="option in selectedOptions"
        :key="option.id"
        class="inline-flex items-center px-2 py-1 bg-n-brand/10 text-n-brand rounded text-xs"
      >
        {{ option.name }}
        <button
          type="button"
          @click="removeOption(option.id, $event)"
          class="ml-1 hover:text-n-brand/70"
          :disabled="disabled"
        >
          ✕
        </button>
      </span>
    </div>

    <!-- Input de búsqueda -->
    <input
      type="text"
      class="w-full px-4 py-3 border border-n-weak rounded-lg 
             focus:ring-2 focus:ring-n-brand focus:border-n-brand 
             outline-none transition-all bg-white dark:bg-n-solid-2 
             text-n-slate-12 text-sm"
      :placeholder="placeholder"
      :disabled="disabled"
      :value="isOpen ? searchQuery : selectedName"
      @input="searchQuery = $event.target.value"
      @focus="handleFocus"
      @blur="handleBlur"
    />

    <!-- Dropdown filtrado -->
    <ul
      v-if="isOpen && filteredOptions.length"
      class="absolute left-0 right-0 bg-white dark:bg-n-solid-2 
             border border-n-weak rounded-lg shadow-lg 
             max-h-60 overflow-auto z-50 mt-1"
    >
      <li
        v-for="opt in filteredOptions"
        :key="opt.id"
        @mousedown.prevent="isOptionDisabled(opt) ? null : selectOption(opt.id)"
        :class="[
          'px-3 py-2 text-sm flex items-center',
          isOptionDisabled(opt) 
            ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800' 
            : 'hover:bg-n-slate-9/10 cursor-pointer',
          isSelected(opt.id) && 'bg-n-brand/5'
        ]"
      >
        <input
          v-if="multiple"
          type="checkbox"
          :checked="isSelected(opt.id)"
          :disabled="isOptionDisabled(opt)"
          class="mr-2"
          @click.stop
        />
        <span :class="{ 'line-through': isOptionDisabled(opt) }">
          {{ opt.name }}
        </span>
      </li>
    </ul>
  </div>
</template>